// Turns what the AI read from an audit plan into template objectives and controls (US 9404).
// The AI keeps every table cell under the document's own column; the user picks, per template field, the column it
// is filled from (one column can fill several fields), so changing the mapping only reruns these functions, never the AI.

// Control fields the import fills, in the order the mapping step shows them
export const CONTROL_FIELDS = [
  'reference',
  'name',
  'description',
  'description_two',
  'data_collection_description',
  'data_collection_description_two',
  'control_description_default_text',
  'performed_test_default_text',
  'conclusion_default_text',
  'testing_notes_default_text'
];
// Fields filled from the control description column by default: the point-in-time (D&I) task and documentation
// collection descriptions and the default control description. The period (OE) descriptions are rarely in an audit
// plan, so they get no suggestion.
const FROM_DESCRIPTION = ['description', 'data_collection_description', 'control_description_default_text'];
const NO_SUGGESTION = ['description_two', 'data_collection_description_two'];

// A field left empty
export const EMPTY = '';
// A field filled with the user's own text, the same in every control
export const CUSTOM = 'custom';
// Fields that can take the user's own text instead of a column
export const CUSTOM_FIELDS = ['conclusion_default_text'];

// Where a control's name can come from besides a column
export const NAME_AI = 'ai';
export const NAME_FIRST_WORDS = 'first_words';
// Where a reference can come from besides a column: as the AI read it (numbered when missing), or always numbered
export const REFERENCE_AUTO = 'auto';
export const REFERENCE_NUMBER = 'number';

// Where an objective's title and description come from
export const OBJECTIVE_TITLE = 'title';
export const OBJECTIVE_HEADING = 'heading';
export const OBJECTIVE_DESCRIPTION = 'description';

// Flags set here, shown in the review step
export const FLAG_REFERENCE_NUMBERED = 'reference_numbered';
export const FLAG_REFERENCE_CHANGED = 'reference_changed';

const NAME_LENGTH = 255;

// The first words of a text, cut at a word boundary, for a control without a title
function shortName(text, length = 80) {
  let line = (text || '').trim().split('\n')[0].replace(/^•\s*/, '');
  if (line.length <= length) {
    return line;
  }
  let cut = line.slice(0, length);
  return cut.slice(0, cut.lastIndexOf(' ') > 40 ? cut.lastIndexOf(' ') : length).trim() + '…';
}

function referenceKey(reference) {
  return (reference || '').trim().toLowerCase();
}

// The column the AI suggests for a template field, if any. The AI names one field per column; its "description"
// column fills the fields in FROM_DESCRIPTION. (Its "description_two" means a requirement column, which no field gets.)
export function suggestedColumn(result, field) {
  if (NO_SUGGESTION.indexOf(field) !== -1) {
    return null;
  }
  let wanted = FROM_DESCRIPTION.indexOf(field) !== -1 ? 'description' : field;
  let suggested = result.suggested_mapping || {};
  return Object.keys(suggested).find(columnId => suggested[columnId] === wanted) || null;
}

// Default settings for a result: the AI's suggestions, AI task titles, references as read, objectives as printed
export function defaultSettings(result) {
  let fields = {};
  CONTROL_FIELDS.forEach(field => {
    fields[field] = suggestedColumn(result, field) || EMPTY;
  });
  fields.reference = fields.reference || REFERENCE_AUTO;
  fields.name = fields.name || NAME_AI;

  return {
    fields: fields,
    // The user's own text per field in CUSTOM_FIELDS, used when the field's source is CUSTOM
    customTexts: {},
    objectiveName: OBJECTIVE_HEADING,
    objectiveDescription: OBJECTIVE_DESCRIPTION
  };
}

// One control in template fields; the reference is completed by applyMapping, which sees all controls
export function mapControl(control, settings) {
  let cells = control.cells || {};
  let fields = {};
  CONTROL_FIELDS.forEach(field => {
    let source = settings.fields[field];
    if (source === CUSTOM) {
      fields[field] = ((settings.customTexts || {})[field] || '').trim();
    } else {
      fields[field] = source && cells[source] !== undefined ? (cells[source] || '').trim() : '';
    }
  });

  let reference = settings.fields.reference;
  fields.reference = reference === REFERENCE_NUMBER ? '' : (fields.reference || control.reference || '').split('\n')[0].trim();

  if (settings.fields.name === NAME_AI) {
    fields.name = control.title || shortName(fields.description);
  } else if (settings.fields.name === NAME_FIRST_WORDS || !fields.name) {
    fields.name = shortName(fields.description);
  }
  fields.name = fields.name.slice(0, NAME_LENGTH);

  return fields;
}

// The whole tree in template fields, with each item's key, page and flags kept for the review step.
// existing = { references, objectives } of the template the import adds to: references are numbered after its
// objectives and never repeat one of its references or each other.
export function applyMapping(result, settings, existing = null) {
  let used = {};
  ((existing && existing.references) || []).forEach(reference => { used[referenceKey(reference)] = true; });
  let firstNumber = ((existing && existing.objectives) || 0) + 1;
  // Numbering is worth a check only where the document has references for the other controls
  let numberedIsNews = settings.fields.reference !== REFERENCE_NUMBER
    && (result.objectives || []).some(objective => (objective.controls || []).some(control => mapControl(control, settings).reference));

  return (result.objectives || []).map((objective, objectiveIndex) => {
    let name = settings.objectiveName === OBJECTIVE_HEADING ? objective.heading || objective.title : objective.title || objective.heading;
    let description = {
      [OBJECTIVE_DESCRIPTION]: objective.description,
      [OBJECTIVE_HEADING]: objective.heading,
      [OBJECTIVE_TITLE]: objective.title
    }[settings.objectiveDescription];

    return {
      key: objective.key,
      reference: objective.reference,
      page: objective.page,
      name: (name || '').slice(0, NAME_LENGTH),
      description: (description || '').trim(),
      controls: (objective.controls || []).map((control, controlIndex) => {
        let fields = mapControl(control, settings);
        let flags = (control.flags || []).slice();

        // Never blank: number it after its objective, e.g. 3.2
        if (!fields.reference) {
          fields.reference = (firstNumber + objectiveIndex) + '.' + (controlIndex + 1);
          if (numberedIsNews) {
            flags.push(FLAG_REFERENCE_NUMBERED);
          }
        }
        // Never repeated: A.1, A.1-2, A.1-3 …
        if (used[referenceKey(fields.reference)]) {
          let base = fields.reference;
          let count = 2;
          while (used[referenceKey(base + '-' + count)]) {
            count++;
          }
          fields.reference = base + '-' + count;
          flags.push(FLAG_REFERENCE_CHANGED);
        }
        used[referenceKey(fields.reference)] = true;

        return Object.assign({ key: control.key, page: control.page, flags: flags }, fields);
      })
    };
  });
}

// Mapped tree with the user's edits and switches applied; off = { key: true } for items left out
export function withEdits(objectives, edits, off) {
  return objectives.map(objective => {
    let controls = objective.controls.map(control => Object.assign({}, control, edits[control.key] || {}, { off: !!off[control.key] }));
    return Object.assign({}, objective, edits[objective.key] || {}, { off: !!off[objective.key], controls: controls });
  });
}

// Problems that stop saving: missing names, descriptions or references, references used twice or already in the
// template, and a period documentation description without the point-in-time one that switches it on
export function problems(objectives, existingReferences = []) {
  let found = {};
  let references = {};
  existingReferences.forEach(reference => { references[referenceKey(reference)] = [null]; });

  objectives.filter(objective => !objective.off).forEach(objective => {
    if (!objective.name.trim()) {
      found[objective.key] = ['no_name'];
    }
    objective.controls.filter(control => !control.off).forEach(control => {
      let list = [];
      if (!control.name.trim()) {
        list.push('no_name');
      }
      if (!control.description.trim()) {
        list.push('no_description');
      }
      if (!control.reference.trim()) {
        list.push('no_reference');
      } else {
        (references[referenceKey(control.reference)] = references[referenceKey(control.reference)] || []).push(control.key);
      }
      if (control.data_collection_description_two.trim() && !control.data_collection_description.trim()) {
        list.push('no_documentation_description');
      }
      if (list.length) {
        found[control.key] = list;
      }
    });
  });
  Object.keys(references).filter(reference => references[reference].length > 1).forEach(reference => {
    references[reference].filter(key => key).forEach(key => { found[key] = (found[key] || []).concat('duplicate_reference'); });
  });
  return found;
}

// What the save endpoint takes: objectives that are on, with the controls that are on
export function payload(objectives) {
  return objectives.filter(objective => !objective.off).map(objective => ({
    name: objective.name.trim(),
    description: (objective.description || '').trim(),
    controls: objective.controls.filter(control => !control.off).map(control => {
      let fields = {};
      CONTROL_FIELDS.forEach(field => { fields[field] = (control[field] || '').trim(); });
      return fields;
    })
  }));
}

// The document's columns no template field is filled from
export function unusedColumns(result, settings) {
  let used = Object.values(settings.fields);
  return (result.columns || []).filter(column => used.indexOf(column.id) === -1);
}
